import CSVFileValidator from 'csv-file-validator'
import constants from 'config/constants'
import React from 'react'
import Papa from 'papaparse'
import { languageOption } from 'organisms/FeedbackFilterGroup/constants'

// Feedback import validates ISO language codes (case-insensitive), matching
// customer import. languageOption maps label -> code, so its values are the codes.
const VALID_LANGUAGE_CODES = new Set(
  Object.values(languageOption).map((code) => code.toLowerCase())
)

interface FieldMapper {
  [key: string]: string | null
}

interface CsvValidationError {
  invalidItemsCount: number
  invalidItems: {
    [rowIndex: number]: string[]
  }
}

export const updateDateFormat = (rows: any[], fieldMapper: FieldMapper | null) => {
  const dateIndex = rows[0].indexOf(fieldMapper?.date)
  return rows.map((row: any) => {
    try {
      if (fieldMapper && fieldMapper.date !== null && dateIndex !== -1) {
        const date = new Date(row[dateIndex])
        if (!isNaN(date.getTime())) {
          row[dateIndex] = date.toISOString()
        }
      }
    } catch (e) {
      console.error(e)
    }
    return row
  })
}

export const validateFeedbackCsvFile = (
  file: string | File | NodeJS.ReadableStream,
  fieldMapper: FieldMapper | null,
  availableChoices: string[],
  csvHeader: string[],
  setCsvFile: React.Dispatch<
    React.SetStateAction<{
      name: string
      data: any
      rawFile: string | File | NodeJS.ReadableStream | null
    }>
  >,
  setInvalidData: React.Dispatch<React.SetStateAction<CsvValidationError>>,
  onSuccess: () => void
) => {
  const processingFile = file

  const validateData = (
    file: string | File | NodeJS.ReadableStream,
    isUpdateCSVFile: boolean,
    rows: any
  ) => {
    const csvConfig = generateCSVConfig(fieldMapper, csvHeader, availableChoices, rows)
    const config = { headers: csvConfig }

    return CSVFileValidator(file, config).then((csvData) => {
      const groupedErrors: { [rowIndex: number]: string[] } = csvData.inValidData.reduce(
        (acc, item) => {
          if (item.rowIndex !== undefined) {
            const rowIndex = item.rowIndex - 1
            if (!acc[rowIndex]) {
              acc[rowIndex] = []
            }
            acc[rowIndex].push(item.message)
          }
          return acc
        },
        {}
      )
      setInvalidData({
        invalidItemsCount: Object.keys(groupedErrors).length,
        invalidItems: groupedErrors,
      })
      onSuccess()
      if (isUpdateCSVFile) {
        setCsvFile((prevState) => ({ ...prevState, data: rows, rawFile: file }))
      }
    })
  }

  if (fieldMapper && fieldMapper.date) {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete(results) {
        let rows = results.data
        rows = updateDateFormat(rows, fieldMapper)
        const csvString = Papa.unparse(rows, {
          header: true,
        })
        const blob = new Blob([csvString], { type: 'text/csv' })
        const processingFile = new File([blob], 'processed.csv', { type: 'text/csv' })
        return validateData(processingFile, true, rows)
      },
    })
  } else {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete(results) {
        return validateData(processingFile, false, results.data)
      },
    })
  }
}

const validationErrorMessage = {
  date: `Invalid date format`,
  rating: `Rating does not match the available choices for the question`,
  is_published: `"Is published" must be true or false`,
  language: (value: string) => `Language '${value}' is not a supported language code`,
  customer_name: `Customer name is too long (max 256 characters)`,
  customer_email: `Customer email is invalid`,
  ticket_id: `Ticket ID is too long (max 256 characters)`,
  ticket_subject: `Ticket subject is too long (max 256 characters)`,
  team_member_id: `Team member ID is too long (max 256 characters)`,
  team_member_name: `Team member name is too long (max 256 characters)`,
  team_member_email: `Team member email is invalid`,
}

const requiredFieldError = (field) =>
  `${field.charAt(0).toUpperCase() + field.slice(1)} can't be empty`

const isEmailValid = (email) => {
  return constants.EMAIL_REGEX.test(email)
}

const isTextValid = (text) => text.length <= 256

const isDateValid = (date) => {
  const parsedDate = new Date(date)
  return !isNaN(parsedDate.getTime())
}

const isChoiceValid = (choice, choices) => choices.includes(choice)

const isBooleanValid = (boolean: string | boolean) => {
  if (typeof boolean === 'boolean') {
    return true
  }
  if (typeof boolean == 'string') {
    return boolean.toLowerCase() === 'true' || boolean.toLowerCase() === 'false'
  }
}

export const isLanguageValid = (value: string) =>
  value.trim() === '' || VALID_LANGUAGE_CODES.has(value.trim().toLowerCase())

const generateCSVConfig = (fieldMapper, csvHeader, ratingChoices, rows?: any[][]) => {
  return csvHeader.map((header) => {
    const key = Object.keys(fieldMapper).find((key) => fieldMapper[key] === header)

    switch (key) {
      case 'rating':
        return {
          name: header,
          inputName: header,
          required: true,
          requiredError: () => requiredFieldError(key),
          validate: (choice) => isChoiceValid(choice, ratingChoices),
          validateError: () => validationErrorMessage[key],
        }

      case 'date':
        return {
          name: header,
          inputName: header,
          required: false,
          validate: isDateValid,
          validateError: () => validationErrorMessage[key],
        }

      case 'comment':
        return {
          name: header,
          inputName: header,
          required: false,
        }

      case 'is_published':
        return {
          name: header,
          inputName: header,
          required: false,
          validate: isBooleanValid,
          validateError: () => validationErrorMessage[key],
        }

      case 'language':
        return {
          name: header,
          inputName: header,
          required: false,
          validate: isLanguageValid,
          validateError: (_, rowIndex, columnIndex) =>
            validationErrorMessage.language(rows?.[rowIndex - 1]?.[columnIndex - 1] ?? ''),
        }

      case 'customer_name':
      case 'ticket_id':
      case 'ticket_subject':
      case 'team_member_id':
      case 'team_member':
        return {
          name: header,
          inputName: header,
          required: false,
          validate: isTextValid,
          validateError: () => validationErrorMessage[key],
        }

      case 'customer_email':
      case 'team_member_email':
        return {
          name: header,
          inputName: header,
          required: false,
          validate: isEmailValid,
          validateError: () => validationErrorMessage[key],
        }

      default:
        return {
          name: header,
          inputName: header,
          required: false,
          validate: null,
        }
    }
  })
}

const OPTIONAL_TRANSLATION_FIELDS = new Set(['confirmation-message-no-sentiment'])

export const generateTranslationCSVConfig = (csvHeader: string[], supportedLanguages: string[]) => {
  const supportedLanguageSet = new Set(supportedLanguages)
  const fieldColumnIndex = csvHeader.findIndex((header) => header.toLowerCase() === 'field')

  const isOptionalRow = (row: string[]) =>
    OPTIONAL_TRANSLATION_FIELDS.has(String(row[fieldColumnIndex] ?? '').toLowerCase())

  return csvHeader.map((header) => {
    if (supportedLanguageSet.has(header.toLowerCase())) {
      return {
        name: header,
        inputName: header,
        required: false,
        dependentValidate: (value: string, row: string[]) =>
          isOptionalRow(row) || Boolean(value && value.trim() !== ''),
        dependentValidateError: () => `${header} have missing rows`,
      }
    }
    return {
      name: header,
      inputName: header,
      required: false,
    }
  })
}

export const validateSurveyLanguageCsvFile = (
  file: string | File | NodeJS.ReadableStream,
  csvHeader: string[],
  supportedLanguages: string[],
  setInvalidLanguage: React.Dispatch<
    React.SetStateAction<Array<{ language: string; error: string }>>
  >
) => {
  const processingFile = file
  const csvConfig = generateTranslationCSVConfig(csvHeader, supportedLanguages)

  const config = {
    headers: csvConfig,
    isHeaderNameOptional: false,
    parserConfig: null,
    isColumnIndexAlphabetic: false,
  }

  const validateData = (file: string | File | NodeJS.ReadableStream) => {
    return CSVFileValidator(file, config).then((csvData) => {
      const languageSpecificError: { [columnIndex: number]: number } = csvData.inValidData.reduce(
        (acc, item) => {
          if (item.columnIndex !== undefined) {
            if (!acc[csvHeader[item.columnIndex - 1]]) {
              acc[csvHeader[item.columnIndex - 1]] = 0
            }
            acc[csvHeader[item.columnIndex - 1]]++
          }
          return acc
        },
        {}
      )
      setInvalidLanguage((prev) => [
        ...prev,
        ...Object.entries(languageSpecificError).map(([language, count]) => ({
          language: language,
          error: `${count} missing rows`,
        })),
      ])
    })
  }
  return validateData(processingFile)
}



// WEBPACK FOOTER //
// ./src/libs/csv-validator/index.tsx